/**
 * ValidationPreview Component
 *
 * Displays CSV validation results in a sortable, filterable table.
 * Uses TanStack Table v8 with standard HTML table elements.
 *
 * Features:
 * - Status badges (green for valid, red for errors)
 * - Error tooltips on hover
 * - Filtering (All/Valid/Errors)
 * - Sorting by columns
 * - Pagination (25 rows per page)
 *
 * @module ImportExportTab/components/ValidationPreview
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  Box,
  Typography,
  Chip,
  Button,
  ButtonGroup,
  Tooltip,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * ValidationPreview Component
 *
 * @param {Object} props
 * @param {Object} props.validationResult - Result from csvValidator service
 * @param {string} props.importType - Type of import ("orgUnits" or "locations")
 * @returns {JSX.Element}
 */
function ValidationPreview({ validationResult, importType }) {
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'valid', 'errors'
  const [sorting, setSorting] = useState([]);

  // Combine valid and invalid records with status flag
  const allRecords = useMemo(() => {
    const valid = validationResult.validRecords.map((record, index) => ({
      ...record,
      status: 'valid',
      rowNumber: index + 2, // 1-indexed, +1 for header
      errors: []
    }));

    const invalid = validationResult.invalidRecords.map(record => ({
      ...record,
      status: 'error'
    }));

    return [...valid, ...invalid];
  }, [validationResult]);

  // Filter records based on selected filter
  const filteredRecords = useMemo(() => {
    if (filterStatus === 'valid') {
      return allRecords.filter(r => r.status === 'valid');
    } else if (filterStatus === 'errors') {
      return allRecords.filter(r => r.status === 'error');
    }
    return allRecords;
  }, [allRecords, filterStatus]);

  // Define columns based on import type
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'rowNumber',
        header: 'Row #',
        size: 80,
        cell: info => info.getValue()
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: info => {
          const status = info.getValue();
          const record = info.row.original;

          if (status === 'valid') {
            return (
              <Chip
                label="Valid"
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
              />
            );
          } else {
            return (
              <Tooltip
                title={
                  <Box>
                    {record.errors.map((err, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        • {err.field}: {err.message}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
              >
                <Chip
                  label="Error"
                  color="error"
                  size="small"
                  icon={<ErrorIcon />}
                />
              </Tooltip>
            );
          }
        }
      }
    ];

    // Add type-specific columns
    if (importType === 'orgUnits') {
      baseColumns.push(
        {
          accessorKey: 'name',
          header: 'Name',
          size: 200
        },
        {
          accessorKey: 'orgLevel',
          header: 'Org Level',
          size: 120
        },
        {
          accessorKey: 'parentOrgUnitName',
          header: 'Parent',
          size: 150
        }
      );
    } else if (importType === 'locations') {
      baseColumns.push(
        {
          accessorKey: 'locationName',
          header: 'Location Name',
          size: 200
        },
        {
          accessorKey: 'street_1',
          header: 'Street Address',
          size: 200,
          cell: info => {
            const street1 = info.getValue();
            const street2 = info.row.original.street_2;
            return street2 ? `${street1}, ${street2}` : street1;
          }
        },
        {
          accessorKey: 'city',
          header: 'City',
          size: 120
        },
        {
          accessorKey: 'state',
          header: 'State',
          size: 80
        }
      );
    }

    return baseColumns;
  }, [importType]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: filteredRecords,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 25
      }
    }
  });

  // Empty state
  if (allRecords.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No records to display. Upload a CSV file to see validation results.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter Controls */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonGroup variant="outlined" size="small">
          <Button
            onClick={() => setFilterStatus('all')}
            variant={filterStatus === 'all' ? 'contained' : 'outlined'}
          >
            All Records ({allRecords.length})
          </Button>
          <Button
            onClick={() => setFilterStatus('valid')}
            variant={filterStatus === 'valid' ? 'contained' : 'outlined'}
            color="success"
          >
            Valid Only ({validationResult.summary.validCount})
          </Button>
          <Button
            onClick={() => setFilterStatus('errors')}
            variant={filterStatus === 'errors' ? 'contained' : 'outlined'}
            color="error"
          >
            Errors Only ({validationResult.summary.invalidCount})
          </Button>
        </ButtonGroup>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      borderBottom: '2px solid #e0e0e0',
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                      userSelect: 'none'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        header.column.getIsSorted() === 'asc' ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        )
                      )}
                    </Box>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                style={{
                  borderBottom: '1px solid #e0e0e0'
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    style={{
                      padding: '12px 16px',
                      verticalAlign: 'middle'
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Pagination */}
      {table.getPageCount() > 1 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </Typography>
          <Box>
            <Button
              size="small"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              sx={{ mr: 1 }}
            >
              Previous
            </Button>
            <Button
              size="small"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

ValidationPreview.propTypes = {
  validationResult: PropTypes.shape({
    validRecords: PropTypes.arrayOf(PropTypes.object).isRequired,
    invalidRecords: PropTypes.arrayOf(PropTypes.object).isRequired,
    summary: PropTypes.shape({
      totalRecords: PropTypes.number.isRequired,
      validCount: PropTypes.number.isRequired,
      invalidCount: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  importType: PropTypes.oneOf(['orgUnits', 'locations']).isRequired
};

export default ValidationPreview;
